/** sessionStorage key guarding against a reload loop when the recovery itself fails. */
const RECOVERY_KEY = "qqt.staleBundleReloadAt";
/** A second failure inside this window means reloading is not fixing it — stop. */
const RECOVERY_WINDOW_MS = 30 * 1000;

/**
 * Recovers a tab whose cached `index.html` points at bundles that are no longer being served.
 *
 * <p>Vite emits `vite:preloadError` when a dynamically imported chunk fails to load. The
 * classic cause is exactly the problem this whole change is about: the browser is running an
 * `index.html` from before a deploy, so the chunk hash it asks for belongs to a build that is
 * gone. A single hard reload fetches the current document and resolves it.
 *
 * <p>QQT ships as one bundle today, so nothing fires this yet — it is wired up now because the
 * day someone adds route-level code splitting, this failure appears in production first and in
 * review never. Note the deploy jobs use `aws s3 cp --recursive` (no `--delete`), so old hashed
 * assets stay in the bucket; that already makes this rare, but it stops being true the first
 * time anyone prunes the bucket.
 *
 * <p>The reload is guarded: if a preload error recurs within 30s of a recovery attempt, we let
 * the error through to the normal error boundary rather than reloading forever.
 */
export const installStaleBundleHandler = (): void => {
  window.addEventListener("vite:preloadError", (event) => {
    const lastAttempt = Number(window.sessionStorage?.getItem(RECOVERY_KEY) ?? 0);
    if (Date.now() - lastAttempt < RECOVERY_WINDOW_MS) return;

    // Suppress Vite's default (rethrow) so the reload isn't racing an unhandled rejection.
    event.preventDefault();
    try {
      window.sessionStorage?.setItem(RECOVERY_KEY, String(Date.now()));
    } catch {
      // Private mode / storage disabled — proceed unguarded rather than not recovering.
    }
    window.location.reload();
  });
};
